import { Typography } from '@mui/material';
import { LightTooltip } from 'lib/designSystem/WhiteTooltip';
import { BASE_COLORS } from 'lib/functions/const/colors';
import { Node } from 'pages/Settings/Components/Risk/Components/DecisionEngines/components/Create/types';
import React, { useState } from 'react';
import Tree from 'react-d3-tree';

import TooltipContent from './components/explanationTooltip';

const buildTree = (nodes: Node[]) => {
  const nodesMap = new Map(
    nodes.map((node) => [node.id, { ...node, children: [] as Node[] }]),
  );

  const buildTreeRecursive = (nodeId: number | undefined) => {
    if (!nodeId) return null;
    const node = nodesMap.get(nodeId);
    if (!node) return null;
    const childrenIds = (node.ranges || []).map((range) => range.nodeId);
    node.children = childrenIds
      .map((childId) => buildTreeRecursive(childId))
      .filter((n) => n !== null) as Node[];
    return node;
  };

  const allChildIds = new Set(
    nodes.flatMap((node) => (node.ranges || []).map((range) => range.nodeId)),
  );
  const rootNodes = nodes.filter((node) => !allChildIds.has(node.id));
  const tree = rootNodes
    .map((rootNode) => buildTreeRecursive(rootNode.id))
    .filter((n) => n !== null) as Node[];
  return tree.length === 1 ? tree[0] : { name: 'Root', children: tree };
};

export const circleColor = (node: Node) => {
  switch (node.type) {
    case 'final':
      switch (node.name) {
        case 'Aprobado':
          return BASE_COLORS.green;
        case 'Rechazado':
          return BASE_COLORS.red;
        default:
          return BASE_COLORS.yellow;
      }
    default:
      return BASE_COLORS.blue;
  }
};

type RenderCircleSvgNodeProps = {
  nodeDatum: Node;
  toggleNode: () => void;
  isHovered: boolean;
  handleHover: (nodeId: number, isHover: boolean) => void;
};

const renderCircleSvgNode = ({
  nodeDatum,
  toggleNode,
  isHovered,
  handleHover,
}: RenderCircleSvgNodeProps) => (
  <g
    onMouseOver={() => handleHover(nodeDatum.id, true)}
    onMouseOut={() => handleHover(nodeDatum.id, false)}
  >
    <circle
      r="20"
      fill={circleColor(nodeDatum)}
      stroke="none"
      onClick={toggleNode}
    />
    <text
      fill="white"
      strokeWidth="0"
      textAnchor="middle"
      x="0"
      y="5"
      fontSize="12px"
    >
      {nodeDatum.id}
    </text>
    <foreignObject
      x={-((nodeDatum.value.length ? nodeDatum.value.length : 7) * 12 + 20)}
      y="-30"
      width="30%"
      height="30"
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-start',
          fontWeight: 'bold',
        }}
      >
        <LightTooltip
          title={<TooltipContent nodeDatum={nodeDatum} />}
          placement="left"
          open={isHovered}
        >
          <div>
            {nodeDatum.fatherNodeId !== 0 && (
              <Typography fontSize={16} variant="body1">
                {nodeDatum.fatherNodeId !== 0 && nodeDatum.value !== ''
                  ? `${nodeDatum.value}`
                  : 'Caso Base'}
              </Typography>
            )}
          </div>
        </LightTooltip>
      </div>
    </foreignObject>
  </g>
);

const TreeVisualization = ({ nodes }: { nodes: Node[] }) => {
  const [hoveredNode, setHoveredNode] = useState<number | null>(null);

  const handleHover = (nodeId: number, isHover: boolean) => {
    setHoveredNode(isHover ? nodeId : null);
  };

  if (!nodes.length) {
    return (
      <div style={{ padding: '20px 0px' }}>
        <Typography variant="body1">
          No hay nodos creados todavía, puedes comenzar a agregarlos aquí a la
          derecha.
        </Typography>
      </div>
    );
  }
  const treeData = buildTree(nodes);

  const containerWidth = 1000;
  const containerHeight = 800;
  const initialTranslate = {
    x: containerWidth / 1.6,
    y: containerHeight / 30,
  };

  return (
    <div id="treeWrapper" style={{ width: '100%', height: '100vh' }}>
      <Tree
        data={treeData}
        zoom={1 / (nodes.length / 12)}
        orientation="vertical"
        pathFunc="diagonal"
        renderCustomNodeElement={(rd3tProps) => {
          const nodeDatum = rd3tProps.nodeDatum as unknown as Node;
          return renderCircleSvgNode({
            nodeDatum,
            toggleNode: rd3tProps.toggleNode,
            isHovered: hoveredNode === nodeDatum.id,
            handleHover,
          });
        }}
        translate={initialTranslate}
      />
    </div>
  );
};

export default TreeVisualization;
